const STATUS_CONFIG = {
  pending: { label: 'Pending', color: '#f59e0b', bg: '#2a1f0a' },
  confirmed: { label: 'Confirmed', color: '#22c55e', bg: '#0a2a14' },
  complete: { label: 'Complete', color: '#6b6b80', bg: '#1a1a24' },
}

export default function JobCard({ job, onComplete, onDelete, onEdit }) {
  const status = STATUS_CONFIG[job.status] || STATUS_CONFIG.pending

  // Supabase can return the timestamp without a trailing 'Z' (e.g. when the
  // column is `timestamp` rather than `timestamptz`). Without that marker,
  // `new Date(...)` assumes the string is already local time and performs
  // no conversion, which showed UTC clock values as if they were local.
  // Appending 'Z' when missing forces correct parsing as UTC, and
  // toLocaleString/toLocaleTimeString then convert it to the browser's
  // local timezone for display, same as the rest of the app expects.
  const rawTime = job.appointment_time
  const isoTime = rawTime.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(rawTime)
    ? rawTime
    : rawTime + 'Z'
  const apptTime = new Date(isoTime)
  const isNoResponse = job.no_show_alert_sent && job.status === 'pending'

  const dateStr = apptTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  const timeStr = apptTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })

  return (
    <div style={{...s.card, borderLeft: `3px solid ${isNoResponse ? '#ef4444' : status.color}`}}>

      {/* Top row: name + badge */}
      <div style={s.topRow}>
        <div style={s.nameGroup}>
          <span style={s.customerName}>{job.customer_name}</span>
          {job.job_number && <span style={s.jobNum}>#{job.job_number}</span>}
        </div>
        <span style={{...s.badge, color: status.color, background: status.bg, flexShrink: 0}}>
          {isNoResponse ? '⚠️ No Response' : status.label}
        </span>
      </div>

      {/* Middle row: time + details */}
      <div style={s.detailsRow}>
        <span style={s.timeText}>{timeStr} · {dateStr}</span>
        {job.service_type && <span style={s.tag}>{job.service_type}</span>}
        {job.tech_name && <span style={s.detail}>👤 {job.tech_name}</span>}
        <span style={s.detail}>📞 {job.customer_phone}</span>
      </div>

      {/* Bottom row: actions */}
      <div style={s.actions}>
        {job.status !== 'complete' && (
          <>
            <button style={s.editBtn} onClick={() => onEdit(job)}>✎ Edit</button>
            <button style={s.completeBtn} onClick={() => onComplete(job.id)}>✓ Done</button>
          </>
        )}
        <button style={s.deleteBtn} onClick={() => onDelete(job.id)}>✕</button>
      </div>

    </div>
  )
}

const s = {
  card: {
    background: '#13131a',
    border: '1px solid #1e1e2e',
    borderRadius: 12,
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 8,
    width: '100%',
  },
  nameGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    flex: 1,
    minWidth: 0,
  },
  customerName: {
    fontWeight: 600,
    fontSize: 16,
    color: '#fff',
    wordBreak: 'break-word',
  },
  jobNum: {
    background: '#1e1e2e',
    color: '#6b6b80',
    fontSize: 11,
    padding: '2px 8px',
    borderRadius: 4,
    flexShrink: 0,
  },
  badge: {
    padding: '3px 10px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  detailsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  timeText: { fontSize: 13, fontWeight: 600, color: '#a0a0b8' },
  tag: {
    background: '#1e1e2e',
    color: '#a0a0b8',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 12,
  },
  detail: { color: '#6b6b80', fontSize: 13 },
  actions: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  editBtn: {
    background: '#1a1a2e',
    color: '#a0a0b8',
    border: '1px solid #2a2a3a',
    borderRadius: 6,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    flex: 1,
  },
  completeBtn: {
    background: '#1a2a1a',
    color: '#22c55e',
    border: '1px solid #22c55e33',
    borderRadius: 6,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    flex: 1,
  },
  deleteBtn: {
    background: '#2a1a1a',
    color: '#ef4444',
    border: '1px solid #ef444433',
    borderRadius: 6,
    padding: '8px 14px',
    fontSize: 13,
    cursor: 'pointer',
  },
}
